import React, { useState } from 'react';
import SEO from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import PriceModal from '../components/PriceModal/PriceModal';
import { sendPriceRequest } from '../api/priceApi';
import { useToast } from '../context/ToastContext';
import './Services.css';

/**
 * Service type constants to avoid duplication and typos
 */
enum SERVICE_TYPES_ENUM {
    INDIVIDUAL = 'INDIVIDUAL',
    FAMILY = 'FAMILY',
    GROUP = 'GROUP',
}

const SERVICE_TYPES = {
    [SERVICE_TYPES_ENUM.INDIVIDUAL]: 'Индивидуальные консультации',
    [SERVICE_TYPES_ENUM.FAMILY]: 'Семейные консультации',
    [SERVICE_TYPES_ENUM.GROUP]: 'Группы поддержки (онлайн)'
};

const Services: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Service descriptions
    const consultation = `Подходят, если вам нужна помощь в сложной жизненной ситуации, эмоциональная поддержка или пространство для осмысления и поиска решений.`;
    const familyConsultation = `Для пар и родственников, которые сталкиваются с кризисами, сложностями в общении или хотят лучше понять друг друга.`;
    const maintainGroup = `Для тех, кто хочет разделить свой опыт с людьми, оказавшимися в похожей ситуации, получить поддержку и новые ресурсы.`;

    // Service durations
    const serviceDurations = {
        [SERVICE_TYPES.INDIVIDUAL]: '50 минут',
        [SERVICE_TYPES.FAMILY]: '90 минут',
        [SERVICE_TYPES.GROUP]: '180 минут'
    };

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentService, setCurrentService] = useState('');

    /**
     * Navigate to contact page with service type
     */
    const handleNavigateToContact = (serviceType: string) => {
        navigate('/contact', { state: { serviceType } });
    };

    /**
     * Open price request modal for a specific service
     */
    const handleOpenPriceModal = (serviceType: SERVICE_TYPES_ENUM) => {
        setCurrentService(SERVICE_TYPES[serviceType]);
        setIsModalOpen(true);
    };

    /**
     * Handle price request submission
     */
    const handlePriceRequest = async (email: string, serviceName: string) => {
        try {
            const serviceType = Object.keys(SERVICE_TYPES).find(key => SERVICE_TYPES[key as SERVICE_TYPES_ENUM] === serviceName) || '';
            await sendPriceRequest({ email, serviceType });
            showToast('Запрос на получение цены успешно отправлен', 'success');
        } catch (error) {
            showToast('Произошла ошибка. Пожалуйста, попробуйте позже', 'error');
            throw error;
        }
    };

    return (
        <>
            <SEO
                title="Услуги | Медицинский психолог Ольга Гальперина"
                description="Индивидуальные и семейные консультации с онлайн психологом. КПТ терапия, психоонкология, кризисное вмешательство. Группы поддержки онлайн."
                keywords="онлайн психолог, кпт психолог, психоонкология, индивидуальные консультации, семейные консультации, группы поддержки"
                canonical="/services"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Service",
                    "name": "Психологические консультации",
                    "provider": {
                        "@type": "Person",
                        "name": "Ольга Гальперина"
                    },
                    "serviceType": ["Индивидуальные консультации", "Семейные консультации", "Группы поддержки"],
                    "areaServed": "Онлайн"
                }}
            />
            <div className="main-content-container">
                <div className="page-header">Услуги</div>
                <div className="main-content main-content-service">
                    <div className="service-container">
                        <div
                            className="service-header interactive-header"
                            onClick={() => handleNavigateToContact(SERVICE_TYPES.INDIVIDUAL)}
                            title="Нажмите, чтобы записаться на консультацию"
                        >
                            {SERVICE_TYPES.INDIVIDUAL}
                        </div>
                        <div
                            className="service-duration">продолжительность {serviceDurations[SERVICE_TYPES.INDIVIDUAL]}</div>
                        <div className="service-content">
                            <div className="service-text">{consultation}</div>
                        </div>
                        <button
                            className="price-button"
                            onClick={() => handleOpenPriceModal(SERVICE_TYPES_ENUM.INDIVIDUAL)}
                        >
                            Узнать цену
                        </button>
                    </div>
                    <div className="divider-container">
                        <div className="divider-service"></div>
                    </div>
                    <div className="service-container">
                        <div
                            className="service-header interactive-header"
                            onClick={() => handleNavigateToContact(SERVICE_TYPES.FAMILY)}
                            title="Нажмите, чтобы записаться на консультацию"
                        >
                            {SERVICE_TYPES.FAMILY}
                        </div>
                        <div
                            className="service-duration">продолжительность {serviceDurations[SERVICE_TYPES.FAMILY]}</div>
                        <div className="service-content">
                            <div className="service-text">{familyConsultation}</div>
                        </div>
                        <button
                            className="price-button"
                            onClick={() => handleOpenPriceModal(SERVICE_TYPES_ENUM.FAMILY)}
                        >
                            Узнать цену
                        </button>
                    </div>
                    <div className="divider-container">
                        <div className="divider-service"></div>
                    </div>
                    <div className="service-container">
                        <div
                            className="service-header interactive-header"
                            onClick={() => handleNavigateToContact(SERVICE_TYPES.GROUP)}
                            title="Нажмите, чтобы записаться на консультацию"
                        >
                            {SERVICE_TYPES.GROUP}
                        </div>
                        <div
                            className="service-duration">продолжительность {serviceDurations[SERVICE_TYPES.GROUP]}</div>
                        <div className="service-content">
                            <div className="service-text">{maintainGroup}</div>
                        </div>
                        <button
                            className="price-button"
                            onClick={() => handleOpenPriceModal(SERVICE_TYPES_ENUM.GROUP)}
                        >
                            Узнать цену
                        </button>
                    </div>
                </div>
            </div>

            {/* Price Request Modal */}
            <PriceModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                serviceName={currentService}
                onSubmit={handlePriceRequest}
            />
        </>
    );
}

export default Services;
